<template>
  <div>
    <div class="login-header">
      <h2>{{ t('registerTitle') }}</h2>
      <button class="close-btn" @click="$emit('back')" :aria-label="t('back')">&times;</button>
    </div>

    <div class="form">
      <div class="form-row">
        <input v-model="firstName" type="text" :placeholder="t('firstName')" />
        <input v-model="lastName" type="text" :placeholder="t('lastName')" />
      </div>
      <input v-model="email" type="email" :placeholder="t('email')" autocomplete="email" />
      <input v-model="phoneNumber" type="tel" :placeholder="t('phoneNumber')" autocomplete="tel" />

      <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
      <div v-if="infoMessage" class="success-message">{{ infoMessage }}</div>

      <!--
        Phase-1: the UI is in place but the server-side self-service
        registration endpoint isn't wired yet (see plan §"Register —
        Phase-1-Risiko"). We render the hint and keep the submit
        button disabled. Once the endpoint exists, swap `enabled`
        to true and the orchestrator will pick up the submit event.
      -->
      <div v-if="!enabled" class="hint">
        {{ t('registerDisabledHint') }}
      </div>

      <button :disabled="!enabled || !canSubmit || busy" @click="submit">
        {{ t('register') }}
      </button>
      <a href="#" class="forgot-link" @click.prevent="$emit('back')">{{ t('backToLogin') }}</a>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { useI18n } from '../i18n/index.js'

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/

export default defineComponent({
  name: 'RegisterView',

  props: {
    busy: { type: Boolean, default: false },
    errorMessage: { type: String, default: '' },
    infoMessage: { type: String, default: '' },
    // Until the backend endpoint is in place this stays false.
    // Phase-2 of the plan flips it.
    enabled: { type: Boolean, default: false }
  },

  emits: ['submit', 'back'],

  setup() {
    const { t } = useI18n()
    return { t }
  },

  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: ''
    }
  },

  computed: {
    canSubmit() {
      return EMAIL_REGEX.test(this.email)
    }
  },

  methods: {
    submit() {
      if (!this.enabled || !this.canSubmit || this.busy) return
      this.$emit('submit', {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        phoneNumber: this.phoneNumber
      })
    }
  }
})
</script>

<style scoped>
.login-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.login-header h2 { margin: 0; font-size: 1.25rem; }
.close-btn {
  border: none; background: none; font-size: 24px; cursor: pointer; color: #6b7280;
}
.close-btn:hover { color: #000; }

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}
/* Mobile-first: stacked. Side-by-side only from 480px (iPhone SE is
   320–375px wide — placeholders like „Vorname" / „Nachname" don't
   fit comfortably next to each other below that). */
.form-row {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.form-row input { flex: 1; width: 100%; box-sizing: border-box; }

input {
  width: 100%;
  box-sizing: border-box;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font: inherit;
  font-size: 16px;
}
button {
  width: 100%;
  padding: 14px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font: inherit;
  font-size: 16px;
  cursor: pointer;
}
button:disabled { background: #c7c9d1; cursor: not-allowed; }

@media (min-width: 480px) {
  .form-row { flex-direction: row; }
}

.hint {
  background: #fff7ed;
  color: #92400e;
  border: 1px solid #fed7aa;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}
.error-message {
  background: #fef2f2; color: #dc2626; border: 1px solid #fecaca;
  padding: 10px 12px; border-radius: 6px; font-size: 13px;
}
.success-message {
  background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0;
  padding: 10px 12px; border-radius: 6px; font-size: 13px;
}
.forgot-link {
  text-align: center; font-size: 13px; color: #6b7280; text-decoration: none;
}
.forgot-link:hover { color: #2563eb; text-decoration: underline; }
</style>
